import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhotoSizeSelectActualIcon from '@mui/icons-material/PhotoSizeSelectActual';
import {Box, Card, CardActions, CardContent, CardMedia, Grid, IconButton, Collapse, Typography} from '@mui/material';

import {CompanyMediaService} from '../services/CompanyMediaService';
import {CompanyProfile} from '../types';

import {CompanyProfileDialogButtons} from './CompanyProfileDialogButtons';
import CompanyProfileImageUpload from './CompanyProfileImageUpload';
import {ConfirmDeleteDialog} from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {useSecureS3Delete} from '../../../../hooks/useSecureS3Delete';
import {useSecureS3Upload} from '../../../../hooks/useSecureS3Upload';
import {empionOrange} from '../../../../layouts/theme/EmpionColors';
import {AppConfig} from '../../../../lib/AppConfig';

type CompanyProfileMediaImagesCardsProps = {
  company: CompanyProfile;
  updateCompany: (company: CompanyProfile) => void;
  setErrorMessage: (message: string | null) => void;
};

export const CompanyProfileMediaImagesCards = ({
  company,
  updateCompany,
  setErrorMessage,
}: CompanyProfileMediaImagesCardsProps) => {
  const [imageRemoveDialogOpen, setImageRemoveDialogOpen] = useState(false);
  const [clicked, setClicked] = useState<any>(false);
  const [imageData, setImageData] = useState<any>(null);
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [imagesMedia, setImages] = useState<any[]>(company.companyMediaFiles.images);
  const {dispatchMessage} = useContext(SnackBarContext);

  const mediaService = new CompanyMediaService();

  const {t} = useTranslation();

  const toggle = (index: any) => {
    if (clicked === index) {
      return setClicked(null);
    }
    setClicked(index);
  };

  const handleCollapse = (index: any, image: string) => {
    toggle(index);
    setImageData(image);
  };

  const handleImageRemoveClick = () => {
    setImageRemoveDialogOpen(true);
  };

  const handleImageRemoveCancel = () => {
    setImageRemoveDialogOpen(false);
    setClicked(null);
  };

  const cancelChanges = () => {
    setNewImageFile(null);
    setImages(imagesMedia.slice(1));
  };

  const createImageMedia = async (companyId: string, mediaURL: string): Promise<any> => {
    const mediaData: any = {
      images: [{sourceUrl: mediaURL}],
    };

    await mediaService.createCompanyMedia(companyId, mediaData).then((res) => {
      setImages(res.companyMediaFiles.images);
    });
  };

  const deleteImageMedia = async (companyId: string, mediaIds: string[]): Promise<any> => {
    await mediaService.deleteCompanyMedia(companyId, mediaIds);
  };

  const onChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = AppConfig.APPLICANT_PROFILE_PICTURE_MAX_SIZE_IN_BYTES;
      if (fileSizeInBytes <= maxSizeInBytes) {
        setErrorMessage(null);
        setNewImageFile(file);
        setImages([...imagesMedia, {sourceUrl: URL.createObjectURL(file)}].reverse());
      } else {
        setErrorMessage(t('companyProfile.header.media.logo.maxFileSizeLimit', {maxSize: 1}));
        setTimeout(() => {
          setErrorMessage(null);
        }, 5000);
      }
    }
  };

  const {
    makeRequest,
    isLoading,
    error: errorUploading,
  } = useSecureS3Upload({
    scope: 'companies',
    ownerId: company.resourceId,
    objectType: 'media',
    file: newImageFile,
    checksum: null,
    onSuccess: (response) => {
      const key = response.url.split('?')[0];
      createImageMedia(company.resourceId, key);
      setNewImageFile(null);
      dispatchMessage(t('shared.formSubmit.success'), 'success');
    },
  });

  const {makeRequest: deleteOldCompanyMediaFromS3, error: errorDeletion} = useSecureS3Delete({
    scope: 'companies',
    ownerId: company.resourceId,
    objectType: 'media',
    onSuccess: async () => {
      await deleteCompanyMedia();
    },
  });

  const deleteCompanyMedia = async (): Promise<any> => {
    const mediaId = imageData.resourceId;
    await deleteImageMedia(company.resourceId, [mediaId]).then(() => {
      const indexToBeRemoved = imagesMedia.findIndex((image) => image.sourceUrl === imageData.sourceUrl);
      if (indexToBeRemoved !== -1) {
        const values = [...imagesMedia];
        values.splice(indexToBeRemoved, 1);
        setImages(values);
        if (isCurrentHeaderImage(imageData)) {
          updateCompanyHeaderImageDeleted();
        }
      }
    });
    dispatchMessage(t('shared.formSubmit.success'), 'success');
    setImageRemoveDialogOpen(false);
    setClicked(null);
    setImageData(null);
  };

  useEffect(() => {
    if (errorDeletion) {
      const message = t('companyProfile.header.media.errorHandling.deleteError');
      dispatchMessage(message, 'error');
    }
  }, [errorDeletion, t, dispatchMessage]);

  useEffect(() => {
    if (errorUploading) {
      const message = t('companyProfile.header.media.errorHandling.uploadError');
      dispatchMessage(message, 'error');
    }
  }, [errorUploading, t, dispatchMessage]);

  const handleUpdateHeaderImage = async (): Promise<any> => {
    return new Promise((resolve) => {
      if (imageData !== undefined) {
        const updatedCompanyData: Partial<CompanyProfile> = {
          resourceId: company.resourceId,
          headerImageUrl: imageData.sourceUrl,
        };

        updateCompany({...company, ...updatedCompanyData});
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      dispatchMessage(t('shared.formSubmit.success'), 'success');
      handleImageRemoveCancel();
      resolve(true);
    });
  };

  const updateCompanyHeaderImageDeleted = async (): Promise<any> => {
    const updatedCompanyData: Partial<CompanyProfile> = {
      resourceId: company.resourceId,
      headerImageUrl: '',
    };

    updateCompany({...company, ...updatedCompanyData});
  };

  const isCurrentHeaderImage = (image: any): boolean => {
    if (image.sourceUrl === company.headerImageUrl) {
      return true;
    }
    return false;
  };

  const imagesCount = imagesMedia.length;
  const minColumnCount = 4;
  const columnCount = imagesCount > 0 ? Math.max(Math.floor(12 / imagesCount), minColumnCount) : 12;

  const onDelete = () => {
    deleteOldCompanyMediaFromS3(imageData.sourceUrl.split('/').pop() as string);
  };

  const renderImageSection = (): any => {
    if (imagesMedia.length === 0) {
      return (
        <>
          <Grid container direction='column' alignItems='center'>
            <Grid item>
              <Typography mb={2} sx={{opacity: 0.3}}>
                {t('companyProfile.header.media.images.emptyState')}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container display={'flex'} justifyContent={'flex-end'}>
            <CompanyProfileImageUpload onChange={onChange} />
          </Grid>
        </>
      );
    } else {
      return (
        <Grid container item spacing={2}>
          {imagesMedia.map((image, index) => (
            <Grid
              item
              xs={12}
              sm={columnCount}
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
              }}
            >
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${empionOrange[3]}`,
                  borderRadius: '12px',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  padding: '10px 10px 7px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '300px',
                }}
              >
                <CardMedia
                  component='img'
                  loading='lazy'
                  image={image.sourceUrl}
                  alt={'Company image'}
                  height='200px'
                  width={'100%'}
                  sx={{
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  sx={{
                    height: '80px',
                  }}
                >
                  <IconButton
                    aria-label='settings'
                    onClick={() => handleCollapse(index, image)}
                    sx={{padding: '16px'}}
                    disabled={newImageFile ? true : false}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  <CardActions>
                    <Collapse in={clicked === index} orientation='horizontal'>
                      <IconButton aria-label='delete' onClick={handleImageRemoveClick}>
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        aria-label='set-header-logo'
                        onClick={handleUpdateHeaderImage}
                        disabled={isCurrentHeaderImage(image)}
                      >
                        <PhotoSizeSelectActualIcon />
                      </IconButton>
                    </Collapse>
                  </CardActions>
                </Box>
              </Card>
            </Grid>
          ))}
          <Grid item container sx={{justifyContent: 'flex-end'}} paddingRight={'10px'}>
            {!newImageFile && imagesMedia.length < 5 && <CompanyProfileImageUpload onChange={onChange} />}
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <>
      <CardContent sx={{padding: '0'}}>{renderImageSection()}</CardContent>
      {newImageFile && (
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '4px',
          }}
        >
          <CompanyProfileDialogButtons onCancel={cancelChanges} onSubmit={makeRequest} isLoading={isLoading} />
        </Grid>
      )}

      <ConfirmDeleteDialog
        open={imageRemoveDialogOpen}
        aria-labelledby='image-remove-alert-dialog-title'
        aria-describedby='image-remove-alert-dialog-description'
        dialogContent={t('companyProfile.header.media.images.removeConfirmation')}
        handleConfirm={onDelete}
        handleCancel={handleImageRemoveCancel}
      />
    </>
  );
};
